import { Form } from '@hologram-dimension/form';
import { RadioField } from '@hologram-dimension/radio-field';
import { TextInputField } from '@hologram-dimension/text-input-field';
import { TextareaField } from '@hologram-dimension/textarea-field';
import useBulkSendSocketMessageCall from 'api/bulkActions/useBulkSendSocketMessageCall';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import type {
  HandlersBulkMessageSimsResponse,
  StructsDataMessage,
} from 'types/__generated__/SimInventoryApi';
import type { HologramSimIdsMap, OnSubmit } from 'types/common';

interface BulkSendDataMessageProps {
  sims: HologramSimIdsMap;
  onProcessing: (value: boolean) => void;
  onDone: (sims?: Array<HologramSimId>) => void;
  onError: (error?: string) => void;
}

interface BulkSendDataMessageFormProps {
  data: string;
  protocol?: StructsDataMessage;
  port: string;
}

const BulkSendDataMessage = forwardRef<OnSubmit, BulkSendDataMessageProps>(
  ({ sims, onProcessing, onDone, onError }, ref) => {
    const doBulkSendSocketMessage = useBulkSendSocketMessageCall();

    const {
      register,
      handleSubmit,
      formState: { errors },
    } = useForm<BulkSendDataMessageFormProps>({
      mode: 'onSubmit',
      shouldFocusError: true,
      reValidateMode: 'onChange',
      defaultValues: { protocol: 'TCP', port: '4010' },
    });

    const commitStateChange = useCallback<SubmitHandler<BulkSendDataMessageFormProps>>(
      async (data) => {
        onProcessing(true);
        let result: HandlersBulkMessageSimsResponse | undefined;
        try {
          result = await doBulkSendSocketMessage({
            body: data.data,
            port: data.port,
            protocol: data.protocol,
            sims,
          });

          if (result?.success) {
            onDone([...sims.keys()]);
          } else {
            onError(result?.error);
          }
        } catch (ex) {
          if (typeof ex === 'string') {
            onError(ex);
          } else {
            onError(`An error occurred: ${ex}`);
            // eslint-disable-next-line no-console
            console.error(ex);
          }
        } finally {
          onProcessing(false);
        }
      },
      [doBulkSendSocketMessage, onDone, onError, onProcessing, sims]
    );

    const onSubmit = handleSubmit(commitStateChange);

    const formRef = useRef<HTMLFormElement>(null);

    const manualSubmit = useCallback(async () => {
      formRef.current?.requestSubmit();
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        onSubmit: manualSubmit,
      }),
      [manualSubmit]
    );

    let dataError: string | undefined;
    if (errors.data) {
      if (errors.data.message && errors.data.message.length > 0) {
        dataError = errors.data.message;
      } else {
        dataError = 'The maximum data length is 160 characters.';
      }
    }

    return (
      <Form onSubmit={onSubmit} ref={formRef}>
        <TextareaField
          required
          label="Data"
          invalid={errors.data !== undefined}
          validationMessage={dataError}
          {...register('data', { required: 'Data is required', maxLength: 160 })}
        />
        <RadioField
          label="Protocol"
          options={[
            { label: 'TCP', value: 'TCP' },
            { label: 'UDP', value: 'UDP' },
          ]}
          {...register('protocol')}
        />
        <TextInputField label="Port" required type="number" {...register('port')} />
      </Form>
    );
  }
);

export default BulkSendDataMessage;
